import React, { useState, useEffect, useCallback } from "react";
import { getDatabase, ref, get } from "firebase/database";
import { Link } from "react-router-dom";

const UserGroupSearch = ({ onSelectUser, onSelectGroup }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState({ users: [], groups: [] });
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Fetch and search users and groups
  const performSearch = useCallback(
    async (query) => {
      if (!query.trim()) {
        setResults({ users: [], groups: [] });
        setShowResults(false);
        return;
      }

      setLoading(true);
      try {
        const db = getDatabase();

        // Fetch all users
        // const usersRef = ref(db, "USERS");
        // const usersSnapshot = await get(usersRef);
        // const usersData = usersSnapshot.val() || {};

        // Convert users object to array - only include id and display name
        // const usersArray = Object.keys(usersData).map((userId) => ({
        //   id: userId,
        //   USERDISPLAYNAME: usersData[userId].USERDISPLAYNAME || "Unknown",
        //   USER_PRIVATE: usersData[userId].USER_PRIVATE || "N",
        // }));

        // Fetch all groups
        const groupsRef = ref(db, "GROUPS");
        const groupsSnapshot = await get(groupsRef);
        const groupsData = groupsSnapshot.val() || {};

        // Convert groups object to array - only include id and group name
        const groupsArray = Object.keys(groupsData).map((groupId) => ({
          id: groupId,
          GROUP_NAME: groupsData[groupId].GROUP_NAME || "Unknown",
          GROUP_PRIVATE: groupsData[groupId].GROUP_PRIVATE || "N",
          GROUP_PUBLIC: groupsData[groupId].GROUP_PUBLIC || "Y",
          GROUP_TYPE: groupsData[groupId].GROUP_TYPE || "Unknown",
          GROUP_SLUG: groupsData[groupId].GROUP_SLUG || "Unknown",
        }));

        // Perform fuzzy search with privacy filter
        const queryLower = query.toLowerCase();
        
        // const matchedUsers = usersArray
        //   .filter((item) => {
        //     const isPrivate = item.USER_PRIVATE === "Y";
        //     if (isPrivate) return false;
        //     const itemValue = item.USERDISPLAYNAME?.toLowerCase() || "";
        //     return itemValue.includes(queryLower);
        //   })
        //   .sort((a, b) => {
        //     const aValue = a.USERDISPLAYNAME?.toLowerCase() || "";
        //     const bValue = b.USERDISPLAYNAME?.toLowerCase() || "";
        //     if (aValue === queryLower) return -1;
        //     if (bValue === queryLower) return 1;
        //     if (aValue.startsWith(queryLower)) return -1;
        //     if (bValue.startsWith(queryLower)) return 1;
        //     return 0;
        //   })
        //   .slice(0, 4);

        // uncomment top when you want to search for users melt then comment this 
        const matchedUsers = [];

        const matchedGroups = groupsArray
          .filter((item) => {
            const isNotPublic = item.GROUP_PUBLIC === "N";
            if (isNotPublic) return false;
            const itemValue = item.GROUP_NAME?.toLowerCase() || "";
            return itemValue.includes(queryLower);
          })
          .sort((a, b) => {
            const aValue = a.GROUP_NAME?.toLowerCase() || "";
            const bValue = b.GROUP_NAME?.toLowerCase() || "";
            if (aValue === queryLower) return -1;
            if (bValue === queryLower) return 1;
            if (aValue.startsWith(queryLower)) return -1;
            if (bValue.startsWith(queryLower)) return 1;
            return 0;
          })
          .slice(0, 4);

        setResults({
          users: matchedUsers,
          groups: matchedGroups,
        });
        setShowResults(true);
      } catch (error) {
        console.error("Error searching:", error);
        setResults({ users: [], groups: [] });
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Debounced search with useEffect for debounce logic
  useEffect(() => {
    // Only search if query is at least 3 characters
    if (searchQuery.trim().length < 3) {
      setResults({ users: [], groups: [] });
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  // const handleUserClick = (user) => {
  //   if (onSelectUser) {
  //     onSelectUser(user);
  //   }
  //   setSearchQuery("");
  //   setShowResults(false);
  // };

  const handleGroupClick = (group) => {
    if (onSelectGroup) {
      onSelectGroup(group);
    }
    setSearchQuery("");
    setShowResults(false);
  };

  return (
    <div style={styles.wrapper} className="desktop-view-button">
      <div style={styles.container}>
        <div style={styles.searchBox}>
          <i className="bi bi-search" style={styles.searchIcon}></i>
          <input
            className="form-control"
            type="text"
            placeholder="Search Groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery && setShowResults(true)}
            style={styles.input}
          />
        </div>

        {loading && <div style={styles.loading}><i className="bi bi-hourglass-split me-2"></i>Searching...</div>}

        {showResults && (results.users.length > 0 || results.groups.length > 0) && (
          <div style={styles.resultsContainer}>
            {/* Groups Section */}
            {results.groups.length > 0 && (
              <div style={styles.section}>
                <ul style={styles.list}>
                  {results.groups.map((group) => (
                    <Link
                      to={`/Group/${group.GROUP_SLUG}`}
                      key={group.id}
                      onClick={() => handleGroupClick(group)}
                      style={styles.listItem}
                    >
                      <i className="bi bi-people-fill" style={styles.listIcon}></i>
                      <span>{group.GROUP_NAME}</span>
                      <span style={styles.badgeType}>{group.GROUP_TYPE}</span>
                    </Link>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {showResults && searchQuery && results.users.length === 0 && results.groups.length === 0 && !loading && (
          <div style={styles.noResults}>
            <i className="bi bi-search me-2"></i>No groups found
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    display: "flex",
    padding: "0",
  },
  container: {
    position: "relative",
    width: "100%",
    maxWidth: "50%",
    minWidth: "300px",
  },
  searchBox: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: "16px",
    fontSize: "18px",
    color: "var(--event-text)",
    pointerEvents: "none",
    zIndex: 2,
  },
  input: {
    width: "100%",
    fontSize: "14px",
    paddingLeft: "44px",
    paddingRight: "16px",
    fontWeight: "500",
    border: "2px solid var(--input-border)",
    borderRadius: "10px",
    backgroundColor: "var(--input-bg)",
    color: "var(--input-color)",
    transition: "all 0.3s ease",
    outline: "none",
  },
  resultsContainer: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    right: 0,
    maxHeight: "500px",
    overflowY: "auto",
    backgroundColor: "var(--card-bg)",
    border: "2px solid var(--input-border)",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
    backdropFilter: "blur(10px)",
  },
  section: {
    padding: "12px 0",
  },
  sectionTitle: {
    margin: "8px 16px 4px 16px",
    fontSize: "12px",
    fontWeight: "bold",
    color: "var(--event-text)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  listItem: {
    padding: "12px 16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    transition: "all 0.2s ease",
    color: "var(--text)",
    textDecoration: "none",
    borderRadius: "8px",
    margin: "4px 8px",
  },
  listIcon: {
    fontSize: "18px",
    color: "var(--color-secondary)",
    flexShrink: 0,
  },
  badgeType: {
    marginLeft: "auto",
    fontSize: "12px",
    fontWeight: "600",
    padding: "4px 10px",
    backgroundColor: "var(--badge-background)",
    color: "var(--badge-text)",
    borderRadius: "20px",
    flexShrink: 0,
    border: "1px solid var(--badge-border)",
  },
  loading: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    right: 0,
    padding: "16px",
    backgroundColor: "var(--card-bg)",
    border: "2px solid var(--input-border)",
    borderRadius: "12px",
    textAlign: "center",
    fontSize: "15px",
    color: "var(--event-text)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
  },
  noResults: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    right: 0,
    padding: "16px",
    backgroundColor: "var(--card-bg)",
    border: "2px solid var(--input-border)",
    borderRadius: "12px",
    textAlign: "center",
    fontSize: "15px",
    color: "var(--event-text)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
  },
};

export default UserGroupSearch;